import {
  CalendarOutlined,
  ClockCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  LockOutlined,
  PlusOutlined,
  ShoppingOutlined,
} from '@ant-design/icons';
import {
  Button,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Tag,
  message,
} from 'antd';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import {useEffect, useMemo, useState} from 'react';
import {useSelector} from 'react-redux';

import {
  RqGetAllFinalProducts,
  RqUpdateBill,
  RqUpdatePayment,
} from '../../../../../reactQuery/apis';

const {Option} = Select;

const fmtCur = v =>
  `₹${Number(v ?? 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
  })}`;

const fmt = iso =>
  iso ? dayjs(iso).format('DD MMM YYYY, hh:mm A') : '—';

const billTypeColor = {
  GST: 'cyan',
  NON_GST: 'geekblue',
};

const PAYMENT_METHODS = [
  'CASH',
  'UPI',
  'CARD',
  'CREDIT',
  'MIXED',
  'CHEQUE',
  'BANK_TRANSFER',
  'ADVANCE',
];

const EMPTY_PRODUCT = () => ({
  _uid: `prod-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
  productId: '',
  productName: '',
  hsnCode: '',
  quantity: 0,
  unitPrice: 0,
  unit: '',
  bags: 0,
  bagsWeight: 0,
  discount: 0,
  cgst: 0,
  sgst: 0,
  igst: 0,
});

const InfoRow = ({label, value, icon}) => (
  <div className={'flex items-start gap-2 py-1'}>
    {icon && (
      <span className={'text-gray-400 mt-0.5 shrink-0'}>{icon}</span>
    )}
    <div>
      <p className={'text-xs text-gray-400 m-0'}>{label}</p>
      <p className={'font-medium text-gray-800 m-0'}>
        {value || '—'}
      </p>
    </div>
  </div>
);

InfoRow.propTypes = {
  label: PropTypes.string,
  value: PropTypes.any,
  icon: PropTypes.node,
};

const LockedBox = ({
  label,
  value,
  color = 'text-gray-700',
}) => (
  <div
    className={
      'bg-slate-50 border border-slate-200 rounded-lg px-3 py-2'
    }
  >
    <p
      className={
        'text-xs text-gray-400 m-0 mb-0.5 flex items-center gap-1'
      }
    >
      <LockOutlined className={'text-xs text-slate-300'} />
      {label}
    </p>
    <p className={`font-bold text-sm m-0 ${color}`}>
      {value}
    </p>
  </div>
);

LockedBox.propTypes = {
  label: PropTypes.string,
  value: PropTypes.any,
  color: PropTypes.string,
};

const EditBill = ({
  open,
  bill,
  token,
  onClose,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [editablePayments, setEditablePayments] = useState([]);
  const [savedPaymentHistory, setSavedPaymentHistory] = useState([]);
  const [editableProducts, setEditableProducts] = useState([]);

  const reduxToken = useSelector(state => state.auth?.tokenData);
  const effectiveToken = token || reduxToken;

  const {data: finalProducts = []} =
    RqGetAllFinalProducts(effectiveToken, {enabled: !!open});

  const productOptions = useMemo(
    () =>
      finalProducts.map(p => ({
        label: p.productName || p.name || `Product #${p.id}`,
        value: p.id,
        data: p,
      })),
    [finalProducts],
  );

  const {
    mutateAsync: updateBillMutation,
    isPending: isUpdatingBill,
  } = RqUpdateBill();

  const {
    mutateAsync: addPayment,
    isPending: isUpdatingPayment,
  } = RqUpdatePayment();

  useEffect(() => {
    if (!bill || !open) {
      return;
    }

    const history = bill.paymentHistory ?? [];

    setSavedPaymentHistory(history);

    setEditablePayments(
      history.map((entry, idx) => ({
        id: `entry-${idx}-${Date.now()}`,
        amount: entry.amount ?? 0,
        paymentDate: entry.paymentDate
          ? dayjs(entry.paymentDate)
          : dayjs(),
        paymentMethod: entry.paymentMethod ?? 'CASH',
        note: entry.note ?? '',
        isExisting: true,
      })),
    );

    /* ── initialise editable products from bill.items ──────────── */
    const billItems = bill.items ?? bill.products ?? [];
    setEditableProducts(
      billItems.map((item, idx) => ({
        _uid: `existing-${idx}-${Date.now()}`,
        productId: item.productId ?? '',
        productName: item.productName ?? '',
        hsnCode: item.hsnCode ?? '',
        quantity: Number(item.quantity) || 0,
        unitPrice: Number(item.unitPrice) || 0,
        unit: item.unit ?? '',
        bags: Number(item.bags) || 0,
        bagsWeight: Number(item.bagsWeight) || 0,
        discount: Number(item.discount) || 0,
        cgst: Number(item.cgst) || 0,
        sgst: Number(item.sgst) || 0,
        igst: Number(item.igst) || 0,
      })),
    );

    const transport = bill.transportDetails ?? {};
    const billDate = dayjs(
      bill.billDate || bill.createdAt || dayjs(),
    );

    const financialYearStart =
      billDate.month() >= 3
        ? billDate.year()
        : billDate.year() - 1;

    const financialYearEnd = financialYearStart + 1;

    const financialYear = `${financialYearStart}-${String(
      financialYearEnd,
    ).slice(-2)}`;

    const sequence = String(bill.id || 1).padStart(4, '0');

    const defaultInvoiceNumber =
      bill.billType === 'GST'
        ? `GST-SI-B-${financialYear}-${sequence}`
        : `NGST-SI-B-${financialYear}-${sequence}`;

    const defaultChallanNumber =
      `CH-SI-${billDate.format('MM')}-${billDate.format(
        'DD',
      )}-${sequence}`;

    form.setFieldsValue({
      billNumber:
        bill.billNumber ||
        bill.invoiceNo ||
        defaultInvoiceNumber,
      paymentMethod: bill.paymentMethod || 'CASH',
      customerName: bill.customerName || '',
      customerMobile: bill.customerMobile || '',
      customerAddress: bill.customerAddress || '',
      businessName:
        bill.businessName && bill.businessName !== 'string'
          ? bill.businessName
          : '',
      gstIn: bill.gstIn || '',
      challanNumber:
        transport.challanNumber || defaultChallanNumber,
      transportName: transport.transportName || '',
      vehicleNumber: transport.vehicleNumber || '',
      driverName: transport.driverName || '',
      driverMobile: transport.driverMobile || '',
      destination: transport.destination || '',
      freightCharge: transport.freightCharge || 0,
      freightPaid: transport.freightPaid ?? false,
    });
  }, [bill, open, form]);

  /* ── product helpers ───────────────────────────────────────────── */
  const addProductSlot = () => {
    setEditableProducts(prev => [...prev, EMPTY_PRODUCT()]);
  };

  const removeProductSlot = uid => {
    Modal.confirm({
      title: 'Remove Product',
      content: 'Are you sure you want to remove this product from the bill?',
      okText: 'Yes, Remove',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: () => {
        setEditableProducts(prev => prev.filter(p => p._uid !== uid));
      },
    });
  };

  const updateProductField = (uid, field, value) => {
    setEditableProducts(prev =>
      prev.map(p => {
        if (p._uid !== uid) {
          return p;
        }

        const updated = {...p, [field]: value};

        /* when product selector changes, auto-fill fields from catalogue */
        if (field === 'productId') {
          const prod = finalProducts.find(fp => fp.id === value);
          if (prod) {
            updated.productName = prod.productName || prod.name || '';
            updated.hsnCode = prod.hsnCode || prod.eanCode || '';
            updated.unit = prod.unit || prod.unitOfMeasure || '';
            updated.unitPrice = prod.sellingPrice ?? prod.price ?? updated.unitPrice;
            updated.cgst = prod.tax?.cgst ?? updated.cgst;
            updated.sgst = prod.tax?.sgst ?? updated.sgst;
            updated.igst = prod.tax?.igst ?? (updated.cgst + updated.sgst);
          }
        }

        /* auto-calc igst = cgst + sgst */
        if (field === 'cgst' || field === 'sgst') {
          const currentCgst = Number(field === 'cgst' ? value : updated.cgst) || 0;
          const currentSgst = Number(field === 'sgst' ? value : updated.sgst) || 0;
          updated.igst = +(currentCgst + currentSgst).toFixed(2);
        }

        /* auto-calc quantity = bags × bagsWeight */
        if (field === 'bagsWeight' || field === 'bags') {
          const bw = Number(field === 'bagsWeight' ? value : updated.bagsWeight) || 0;
          const b = Number(field === 'bags' ? value : updated.bags) || 0;
          if (bw > 0 && b > 0) {
            updated.quantity = +(bw * b).toFixed(2);
          }
        }

        return updated;
      }),
    );
  };

  const validateProducts = () => {
    for (let i = 0; i < editableProducts.length; i++) {
      const p = editableProducts[i];
      if (!p.productName?.trim()) {
        message.error(`Product #${i + 1}: Product Name is required`);
        return false;
      }
      const numericFields = [
        {key: 'quantity', label: 'Quantity'},
        {key: 'unitPrice', label: 'Unit Price'},
        {key: 'bags', label: 'Bags'},
        {key: 'bagsWeight', label: 'Bags Weight'},
        {key: 'discount', label: 'Discount'},
        {key: 'cgst', label: 'CGST'},
        {key: 'sgst', label: 'SGST'},
        {key: 'igst', label: 'IGST'},
      ];
      for (const {key, label} of numericFields) {
        const val = Number(p[key]);
        if (isNaN(val) || val < 0) {
          message.error(`Product #${i + 1}: ${label} must be a number ≥ 0`);
          return false;
        }
      }
    }
    return true;
  };

  /* ── payment helpers ───────────────────────────────────────────── */
  const addPaymentSlot = () => {
    setEditablePayments(prev => [
      ...prev,
      {
        id: `new-${Date.now()}-${Math.random()}`,
        amount: 0,
        paymentDate: dayjs(),
        paymentMethod: 'CASH',
        note: '',
        isExisting: false,
      },
    ]);
  };

  const removePaymentSlot = id => {
    const paymentToRemove = editablePayments.find(
      p => p.id === id,
    );

    if (paymentToRemove?.isExisting) {
      Modal.confirm({
        title: 'Remove Payment Entry',
        content:
          'This will remove this payment from the bill. Are you sure?',
        okText: 'Yes, Remove',
        okType: 'danger',
        cancelText: 'Cancel',
        onOk: () => {
          setEditablePayments(prev =>
            prev.filter(p => p.id !== id),
          );
        },
      });
    } else {
      setEditablePayments(prev =>
        prev.filter(p => p.id !== id),
      );
    }
  };

  const updateSlot = (id, field, value) => {
    setEditablePayments(prev =>
      prev.map(p =>
        p.id === id ? {...p, [field]: value} : p,
      ),
    );
  };

  const totalEditablePaid = editablePayments.reduce(
    (sum, p) => sum + (Number(p.amount) || 0),
    0,
  );

  const pendingAfterUpdate = Math.max(
    0,
    (bill?.finalAmount ?? 0) - totalEditablePaid,
  );

  const validatePayments = () => {
    for (const slot of editablePayments) {
      if (!slot.amount || slot.amount <= 0) {
        message.error(
          'Each payment entry must have an amount greater than 0',
        );
        return false;
      }
    }

    if (totalEditablePaid > (bill?.finalAmount ?? 0)) {
      message.error(
        `Paid Amount (${fmtCur(
          totalEditablePaid,
        )}) exceeds final amount (${fmtCur(
          bill?.finalAmount,
        )})`,
      );
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (
        editablePayments.length > 0 &&
        !validatePayments()
      ) {
        return;
      }

      if (!validateProducts()) {
        return;
      }

      const transport = {
        ...(bill?.transportDetails || {}),
        challanNumber: values.challanNumber || '',
        transportName: values.transportName || '',
        vehicleNumber: values.vehicleNumber || '',
        driverName: values.driverName || '',
        driverMobile: values.driverMobile || '',
        destination: values.destination || '',
        freightCharge: Number(values.freightCharge || 0),
        freightPaid: !!values.freightPaid,
        enabled: !!(
          values.transportName ||
          values.challanNumber ||
          bill?.transportDetails?.enabled
        ),
      };

      /* ── build products array for payload ────────────────────── */
      const productsPayload = editableProducts.map(p => ({
        productId: p.productId || '',
        productName: p.productName || '',
        hsnCode: p.hsnCode || '',
        quantity: Number(p.quantity) || 0,
        unitPrice: Number(p.unitPrice) || 0,
        unit: p.unit || '',
        bags: Number(p.bags) || 0,
        bagsWeight: Number(p.bagsWeight) || 0,
        discount: Number(p.discount) || 0,
        cgst: Number(p.cgst) || 0,
        sgst: Number(p.sgst) || 0,
        igst: Number(p.igst) || 0,
      }));

      const billData = {
        ...bill,
        billNumber: values.billNumber,
        paymentMethod: values.paymentMethod,
        customerName: values.customerName,
        customerMobile: values.customerMobile,
        customerAddress: values.customerAddress,
        businessName: values.businessName || '',
        gstIn: values.gstIn || '',
        transportDetails: transport,
        items: productsPayload,
        products: productsPayload,
      };

      const requestBody = {
        id: bill.id,
        billData,
        token,
      };

      console.log('📤 UPDATE BILL REQUEST BODY:', requestBody);

      try {
        const response = await updateBillMutation(requestBody);

        console.log('📥 UPDATE BILL RESPONSE BODY:', response);
      } catch (error) {
        console.error(
          '❌ UPDATE BILL ERROR:',
          error?.response?.data || error,
        );
      }

      const newPayments = editablePayments.filter(
        p => !p.isExisting,
      );

      for (const payment of newPayments) {
        await addPayment({
          billId: bill.id,
          token,
          paymentData: {
            amount: Number(payment.amount),
            paymentMethod: payment.paymentMethod,
            paymentDate: payment.paymentDate
              ? payment.paymentDate.toISOString()
              : new Date().toISOString(),
            note: payment.note || '',
          },
        });
      }

      message.success('Bill updated successfully');
      onSuccess?.();
      onClose();
    } catch (err) {
      message.error(
        err?.response?.data?.message ||
        err?.message ||
        'Failed to update bill',
      );
    }
  };

  if (!bill) {
    return null;
  }

  const hasPaymentChanges = () => {
    if (
      editablePayments.length !==
      savedPaymentHistory.length
    ) {
      return true;
    }

    for (let i = 0; i < editablePayments.length; i++) {
      const editable = editablePayments[i];
      const saved = savedPaymentHistory[i];

      if (!saved) {
        return true;
      }

      if (
        editable.amount !== saved.amount ||
        editable.paymentMethod !== saved.paymentMethod ||
        editable.note !== saved.note
      ) {
        return true;
      }
    }

    return false;
  };

  const hasChanges = hasPaymentChanges();

  return (
    <Modal
      open={open}
      onCancel={onClose}
      destroyOnHidden
      width={860}
      title={(
        <div className={'flex items-center gap-2'}>
          <EditOutlined className={'text-primary-600'} />
          <span className={'Subtitle'}>
            Edit Bill —{' '}
            <span className={'font-mono text-primary-600'}>
              {bill.billNumber}
            </span>
          </span>
          <Tag
            color={billTypeColor[bill.billType] ?? 'default'}
            className={'ml-1 text-xs'}
          >
            {bill.billType?.replace('_', ' ')}
          </Tag>
          {hasChanges && (
            <Tag color={'orange'} className={'ml-2'}>
              <ClockCircleOutlined /> Unsaved Changes
            </Tag>
          )}
        </div>
      )}
      styles={{
        body: {
          maxHeight: '80vh',
          overflowY: 'auto',
          padding: '16px 24px',
        },
      }}
      footer={(
        <div className={'flex gap-2 justify-end py-1'}>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            loading={isUpdatingBill || isUpdatingPayment}
            onClick={handleSubmit}
            className={'btn-primary'}
          >
            Save Changes
          </Button>
        </div>
      )}
    >
      <Form
        form={form}
        layout={'vertical'}
        size={'small'}
      >
        {/* ── Bill Information ──────────────────────────────────── */}
        <div
          className={
            'bg-primary-50 rounded-md p-4 mb-4 border border-primary-100'
          }
        >
          <p className={'Subtitle mb-3'}>📄 Bill Information</p>
          <Row gutter={[12, 8]}>
            <Col xs={24} sm={12} md={8}>
              <Form.Item
                label={'Invoice Number'}
                name={'billNumber'}
                rules={[
                  {
                    required: true,
                    message: 'Invoice number is required',
                  },
                  {
                    pattern:
                      /^(GST|NGST)-SI-B-\d{4}-\d{2}-\d{2}-\d{4}$/,
                    message:
                      'Invoice number must follow GST-SI-B-2026-27-0001 or NGST-SI-B-2026-27-0001',
                  },
                ]}
              >
                <Input
                  placeholder={'GST-SI-B-2026-27-0001'}
                  className={'font-mono'}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={8}>
              <Form.Item
                label={'Payment Method'}
                name={'paymentMethod'}
                rules={[
                  {
                    required: true,
                    message: 'Payment method is required',
                  },
                ]}
              >
                <Select
                  options={PAYMENT_METHODS.map(m => ({
                    label: m.replace('_', ' '),
                    value: m,
                  }))}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={8}>
              <InfoRow
                label={'Bill Date'}
                value={fmt(
                  bill.billDate ?? bill.createdAt,
                )}
                icon={<CalendarOutlined />}
              />
              <InfoRow
                label={'Status'}
                value={bill.billStatus}
                icon={<ClockCircleOutlined />}
              />
            </Col>
          </Row>
        </div>

        {/* ── Customer Details ──────────────────────────────────── */}
        <div
          className={
            'bg-blue-50 rounded-md p-4 mb-4 border border-blue-100'
          }
        >
          <p className={'Subtitle mb-3'}>👤 Customer Details</p>
          <Row gutter={[12, 8]}>
            <Col xs={24} sm={12}>
              <Form.Item
                label={'Customer Name'}
                name={'customerName'}
                rules={[
                  {
                    required: true,
                    message: 'Customer name is required',
                  },
                ]}
              >
                <Input placeholder={'Customer Name'} />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12}>
              <Form.Item
                label={'Mobile'}
                name={'customerMobile'}
                rules={[
                  {
                    required: true,
                    message: 'Mobile is required',
                  },
                  {
                    pattern: /^[6-9]\d{9}$/,
                    message:
                      'Enter a valid 10-digit mobile number',
                  },
                ]}
              >
                <Input
                  placeholder={'9876543210'}
                  maxLength={10}
                />
              </Form.Item>
            </Col>

            <Col xs={24}>
              <Form.Item
                label={'Billing Address'}
                name={'customerAddress'}
                rules={[
                  {
                    required: true,
                    message: 'Address is required',
                  },
                ]}
              >
                <Input placeholder={'Address'} />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12}>
              <Form.Item
                label={'Business Name'}
                name={'businessName'}
              >
                <Input placeholder={'e.g. ABC Traders'} />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12}>
              <Form.Item
                label={'GSTIN'}
                name={'gstIn'}
              >
                <Input
                  placeholder={'27ABCDE1234F1Z5'}
                  className={'font-mono uppercase'}
                  maxLength={15}
                />
              </Form.Item>
            </Col>
          </Row>

          {bill.shippingAddress?.city && (
            <div
              className={
                'mt-2 bg-white/60 rounded-lg px-3 py-2 text-xs text-gray-600'
              }
            >
              <b>Shipping:</b>{' '}
              {[
                bill.shippingAddress.address,
                bill.shippingAddress.city,
                bill.shippingAddress.district,
                bill.shippingAddress.pinCode,
              ]
                .filter(Boolean)
                .join(', ')}
            </div>
          )}
        </div>

        {/* ── Product Details / Edit Products ──────────────────── */}
        <div
          className={
            'bg-amber-50/60 rounded-md p-0 mb-4 border border-amber-200 overflow-hidden'
          }
        >
          <div
            className={
              'px-4 py-3 bg-amber-100/80 border-b border-amber-200 flex items-center justify-between'
            }
          >
            <div>
              <p className={'Subtitle m-0'}>
                <ShoppingOutlined className={'mr-2'} />
                Product Details / Edit Products
              </p>
              <p
                className={
                  'text-xs text-amber-600 m-0 mt-0.5'
                }
              >
                {editableProducts.length} product{editableProducts.length !== 1 ? 's' : ''} in this bill
              </p>
            </div>

            <Button
              size={'small'}
              icon={<PlusOutlined />}
              onClick={addProductSlot}
              className={'btn-secondary'}
            >
              Add Product
            </Button>
          </div>

          <div className={'divide-y divide-amber-100'}>
            {editableProducts.length === 0 && (
              <div
                className={
                  'px-4 py-6 text-center text-gray-400 text-sm'
                }
              >
                No products in this bill.{' '}
                <button
                  className={
                    'text-amber-600 underline cursor-pointer bg-transparent border-0 p-0'
                  }
                  onClick={addProductSlot}
                >
                  Add the first product.
                </button>
              </div>
            )}

            {editableProducts.map((prod, idx) => (
              <div
                key={prod._uid}
                className={'px-4 py-3 bg-white'}
              >
                {/* product header */}
                <div
                  className={
                    'flex items-center justify-between mb-3'
                  }
                >
                  <div className={'flex items-center gap-2'}>
                    <span
                      className={
                        'text-xs font-semibold text-amber-700 uppercase tracking-wide'
                      }
                    >
                      Product #{idx + 1}
                    </span>
                    {prod.productName && (
                      <Tag
                        color={'blue'}
                        className={'text-xs m-0'}
                      >
                        {prod.productName}
                      </Tag>
                    )}
                  </div>

                  <button
                    onClick={() => removeProductSlot(prod._uid)}
                    className={
                      'flex items-center gap-1 text-xs text-red-400 hover:text-red-600 bg-transparent border-0 cursor-pointer p-0 transition-colors'
                    }
                  >
                    <DeleteOutlined /> Remove Product
                  </button>
                </div>

                {/* product fields */}
                <Row gutter={[12, 8]}>
                  {/* Product selector */}
                  <Col xs={24} sm={12} md={8}>
                    <p className={'text-xs text-gray-500 mb-1 font-medium'}>
                      Product
                    </p>
                    <Select
                      className={'w-full'}
                      showSearch
                      allowClear
                      placeholder={'Select product…'}
                      value={prod.productId || undefined}
                      filterOption={(input, option) =>
                        (option?.label ?? '')
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      options={productOptions}
                      onChange={val =>
                        updateProductField(prod._uid, 'productId', val ?? '')
                      }
                    />
                  </Col>

                  {/* Product Name */}
                  <Col xs={24} sm={12} md={8}>
                    <p className={'text-xs text-gray-500 mb-1 font-medium'}>
                      Product Name <span className={'text-red-400'}>*</span>
                    </p>
                    <Input
                      value={prod.productName}
                      placeholder={'Product name'}
                      onChange={e =>
                        updateProductField(prod._uid, 'productName', e.target.value)
                      }
                    />
                  </Col>

                  {/* HSN Code */}
                  <Col xs={24} sm={12} md={8}>
                    <p className={'text-xs text-gray-500 mb-1 font-medium'}>
                      HSN Code
                    </p>
                    <Input
                      value={prod.hsnCode}
                      placeholder={'e.g. 33049940'}
                      className={'font-mono'}
                      onChange={e =>
                        updateProductField(prod._uid, 'hsnCode', e.target.value)
                      }
                    />
                  </Col>

                  {/* Quantity */}
                  <Col xs={24} sm={8} md={6}>
                    <p className={'text-xs text-gray-500 mb-1 font-medium'}>
                      Quantity
                    </p>
                    <InputNumber
                      className={'w-full'}
                      min={0}
                      step={0.01}
                      value={prod.quantity}
                      placeholder={'0'}
                      onChange={val =>
                        updateProductField(prod._uid, 'quantity', val ?? 0)
                      }
                    />
                  </Col>

                  {/* Unit Price */}
                  <Col xs={24} sm={8} md={6}>
                    <p className={'text-xs text-gray-500 mb-1 font-medium'}>
                      Unit Price (₹)
                    </p>
                    <InputNumber
                      className={'w-full'}
                      min={0}
                      step={0.01}
                      prefix={'₹'}
                      value={prod.unitPrice}
                      placeholder={'0.00'}
                      onChange={val =>
                        updateProductField(prod._uid, 'unitPrice', val ?? 0)
                      }
                    />
                  </Col>

                  {/* Unit */}
                  <Col xs={24} sm={8} md={6}>
                    <p className={'text-xs text-gray-500 mb-1 font-medium'}>
                      Unit
                    </p>
                    <Input
                      value={prod.unit}
                      placeholder={'e.g. KG, PCS'}
                      onChange={e =>
                        updateProductField(prod._uid, 'unit', e.target.value)
                      }
                    />
                  </Col>

                  {/* Bags */}
                  <Col xs={24} sm={8} md={6}>
                    <p className={'text-xs text-gray-500 mb-1 font-medium'}>
                      Bags
                    </p>
                    <InputNumber
                      className={'w-full'}
                      min={0}
                      value={prod.bags}
                      placeholder={'0'}
                      onChange={val =>
                        updateProductField(prod._uid, 'bags', val ?? 0)
                      }
                    />
                  </Col>

                  {/* Bags Weight */}
                  <Col xs={24} sm={8} md={6}>
                    <p className={'text-xs text-gray-500 mb-1 font-medium'}>
                      Bags Weight
                    </p>
                    <InputNumber
                      className={'w-full'}
                      min={0}
                      step={0.01}
                      value={prod.bagsWeight}
                      placeholder={'0'}
                      onChange={val =>
                        updateProductField(prod._uid, 'bagsWeight', val ?? 0)
                      }
                    />
                  </Col>

                  {/* Discount */}
                  <Col xs={24} sm={8} md={6}>
                    <p className={'text-xs text-gray-500 mb-1 font-medium'}>
                      Discount
                    </p>
                    <InputNumber
                      className={'w-full'}
                      min={0}
                      step={0.01}
                      value={prod.discount}
                      placeholder={'0'}
                      onChange={val =>
                        updateProductField(prod._uid, 'discount', val ?? 0)
                      }
                    />
                  </Col>

                  {/* CGST */}
                  <Col xs={24} sm={8} md={6}>
                    <p className={'text-xs text-gray-500 mb-1 font-medium'}>
                      CGST (%)
                    </p>
                    <InputNumber
                      className={'w-full'}
                      min={0}
                      step={0.01}
                      value={prod.cgst}
                      placeholder={'0'}
                      onChange={val =>
                        updateProductField(prod._uid, 'cgst', val ?? 0)
                      }
                    />
                  </Col>

                  {/* SGST */}
                  <Col xs={24} sm={8} md={6}>
                    <p className={'text-xs text-gray-500 mb-1 font-medium'}>
                      SGST (%)
                    </p>
                    <InputNumber
                      className={'w-full'}
                      min={0}
                      step={0.01}
                      value={prod.sgst}
                      placeholder={'0'}
                      onChange={val =>
                        updateProductField(prod._uid, 'sgst', val ?? 0)
                      }
                    />
                  </Col>

                  {/* IGST */}
                  <Col xs={24} sm={8} md={6}>
                    <p className={'text-xs text-gray-500 mb-1 font-medium'}>
                      IGST (%)
                    </p>
                    <InputNumber
                      className={'w-full'}
                      disabled
                      min={0}
                      step={0.01}
                      value={prod.igst}
                      placeholder={'0'}
                      onChange={val =>
                        updateProductField(prod._uid, 'igst', val ?? 0)
                      }
                    />
                  </Col>

                  {/* Line Total (read-only) */}
                  <Col xs={24} sm={8} md={6}>
                    <p className={'text-xs text-gray-500 mb-1 font-medium'}>
                      Line Total
                    </p>
                    <div
                      className={
                        'bg-slate-50 border border-slate-200 rounded-md px-3 py-1.5 text-sm font-bold text-primary-700'
                      }
                    >
                      {fmtCur(
                        (Number(prod.quantity) || 0) *
                        (Number(prod.unitPrice) || 0),
                      )}
                    </div>
                  </Col>
                </Row>
              </div>
            ))}
          </div>

          {/* product summary footer */}
          {editableProducts.length > 0 && (
            <div
              className={
                'px-4 py-3 bg-amber-100/60 border-t border-amber-200 flex flex-wrap gap-4 items-center justify-between'
              }
            >
              <div className={'flex gap-4 text-sm'}>
                <span className={'text-amber-700'}>
                  Products: <b>{editableProducts.length}</b>
                </span>
                <span className={'text-gray-500'}>
                  Total Qty:{' '}
                  <b>
                    {editableProducts
                      .reduce((s, p) => s + (Number(p.quantity) || 0), 0)
                      .toFixed(2)}
                  </b>
                </span>
              </div>
              <div
                className={
                  'rounded-lg px-4 py-1.5 text-sm font-bold bg-amber-50 text-amber-700 border border-amber-200'
                }
              >
                Subtotal:{' '}
                {fmtCur(
                  editableProducts.reduce((s, p) => s + (Number(p.quantity) || 0) * (Number(p.unitPrice) || 0), 0),
                )}
              </div>
            </div>
          )}
        </div>

        {/* ── Transport / Delivery Details ──────────────────────── */}
        <div
          className={
            'bg-emerald-50/50 rounded-md p-4 mb-4 border border-emerald-100'
          }
        >
          <p className={'Subtitle mb-3'}>
            🚛 Transport / Delivery Details
          </p>
          <Row gutter={[12, 8]}>
            <Col xs={24} sm={8}>
              <Form.Item
                label={'Challan Number'}
                name={'challanNumber'}
                rules={[
                  {
                    pattern:
                      /^CH-SI-\d{2}-\d{2}-\d{4}$/,
                    message:
                      'Challan number must follow the format CH-SI-MM-DD-0001',
                  },
                ]}
              >
                <Input
                  placeholder={'CH-SI-09-09-0001'}
                  className={'font-mono'}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={8}>
              <Form.Item
                label={'Transport Name'}
                name={'transportName'}
              >
                <Input placeholder={'e.g. Shri Ram Logistics'} />
              </Form.Item>
            </Col>

            <Col xs={24} sm={8}>
              <Form.Item
                label={'Vehicle Number'}
                name={'vehicleNumber'}
              >
                <Input
                  placeholder={'e.g. MH 11 A 1836'}
                  className={'uppercase'}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={8}>
              <Form.Item
                label={'Driver Name'}
                name={'driverName'}
              >
                <Input placeholder={'Driver Name'} />
              </Form.Item>
            </Col>

            <Col xs={24} sm={8}>
              <Form.Item
                label={'Driver Mobile'}
                name={'driverMobile'}
              >
                <Input
                  placeholder={'9876543210'}
                  maxLength={10}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={8}>
              <Form.Item
                label={'Destination'}
                name={'destination'}
              >
                <Input placeholder={'Destination'} />
              </Form.Item>
            </Col>
          </Row>
        </div>
      </Form>

      {/* ── Bill Amounts ────────────────────────────────────────── */}
      <div
        className={
          'bg-slate-50 rounded-md p-4 mb-4 border border-slate-200'
        }
      >
        <p className={'Subtitle mb-3'}>💰 Bill Amounts</p>
        <div className={'grid grid-cols-2 md:grid-cols-3 gap-3'}>
          <LockedBox
            label={'Total Payments'}
            value={fmtCur(totalEditablePaid)}
            color={'text-gray-800'}
          />
          <LockedBox
            label={'Final Amount'}
            value={fmtCur(bill.finalAmount)}
            color={'text-primary-700'}
          />
          <LockedBox
            label={'Pending Amount'}
            value={fmtCur(pendingAfterUpdate)}
            color={
              pendingAfterUpdate > 0
                ? 'text-red-600'
                : 'text-green-600'
            }
          />
        </div>
      </div>

      <Divider className={'my-3'} />

      {/* ── Payment Entries ─────────────────────────────────────── */}
      <div
        className={
          'bg-primary-50 rounded-md border border-primary-200 overflow-hidden'
        }
      >
        <div
          className={
            'px-4 py-3 bg-primary-100 border-b border-primary-200 flex items-center justify-between'
          }
        >
          <div>
            <p className={'Subtitle m-0'}>
              <EditOutlined className={'mr-2'} />
              Edit Payment Entries
            </p>
            <p
              className={
                'text-xs text-primary-500 m-0 mt-0.5'
              }
            >
              Modify existing payments or add new instalments
            </p>
          </div>

          <Button
            size={'small'}
            icon={<PlusOutlined />}
            onClick={addPaymentSlot}
            className={'btn-secondary'}
          >
            Add Payment
          </Button>
        </div>

        <div className={'divide-y divide-primary-100'}>
          {editablePayments.length === 0 && (
            <div
              className={
                'px-4 py-6 text-center text-gray-400 text-sm'
              }
            >
              No payment entries yet.{' '}
              <button
                className={
                  'text-primary-600 underline cursor-pointer bg-transparent border-0 p-0'
                }
                onClick={addPaymentSlot}
              >
                Add the first one.
              </button>
            </div>
          )}

          {editablePayments.map((slot, idx) => (
            <div
              key={slot.id}
              className={'px-4 py-3 bg-white'}
            >
              <div
                className={
                  'flex items-center justify-between mb-2'
                }
              >
                <div className={'flex items-center gap-2'}>
                  <span
                    className={
                      'text-xs font-semibold text-primary-600 uppercase tracking-wide'
                    }
                  >
                    Payment #{idx + 1}
                  </span>

                  {slot.isExisting ? (
                    <Tag
                      color={'blue'}
                      className={'text-xs m-0'}
                    >
                      Existing
                    </Tag>
                  ) : (
                    <Tag
                      color={'orange'}
                      className={'text-xs m-0'}
                    >
                      New
                    </Tag>
                  )}
                </div>

                <button
                  onClick={() =>
                    removePaymentSlot(slot.id)
                  }
                  className={
                    'flex items-center gap-1 text-xs text-red-400 hover:text-red-600 bg-transparent border-0 cursor-pointer p-0 transition-colors'
                  }
                >
                  <DeleteOutlined /> Remove
                </button>
              </div>

              <Row gutter={[12, 8]} align={'bottom'}>
                <Col xs={24} sm={6}>
                  <p
                    className={
                      'text-xs text-gray-500 mb-1 font-medium'
                    }
                  >
                    Amount (₹){' '}
                    <span className={'text-red-400'}>*</span>
                  </p>

                  <InputNumber
                    className={'w-full'}
                    min={0}
                    step={0.01}
                    max={bill.finalAmount}
                    prefix={'₹'}
                    value={slot.amount}
                    placeholder={'0.00'}
                    onChange={val =>
                      updateSlot(
                        slot.id,
                        'amount',
                        val,
                      )
                    }
                    status={
                      !slot.amount || slot.amount <= 0
                        ? 'error'
                        : ''
                    }
                  />
                </Col>

                <Col xs={24} sm={6}>
                  <p
                    className={
                      'text-xs text-gray-500 mb-1 font-medium'
                    }
                  >
                    Payment Date{' '}
                    <span className={'text-red-400'}>*</span>
                  </p>

                  <DatePicker
                    className={'w-full'}
                    value={slot.paymentDate}
                    showTime={{format: 'HH:mm'}}
                    format={'DD MMM YYYY, HH:mm'}
                    onChange={val =>
                      updateSlot(
                        slot.id,
                        'paymentDate',
                        val,
                      )
                    }
                  />
                </Col>

                <Col xs={24} sm={6}>
                  <p
                    className={
                      'text-xs text-gray-500 mb-1 font-medium'
                    }
                  >
                    Method
                  </p>

                  <Select
                    className={'w-full'}
                    value={slot.paymentMethod}
                    onChange={val =>
                      updateSlot(
                        slot.id,
                        'paymentMethod',
                        val,
                      )
                    }
                  >
                    {PAYMENT_METHODS.map(m => (
                      <Option key={m} value={m}>
                        {m.replace('_', ' ')}
                      </Option>
                    ))}
                  </Select>
                </Col>

                <Col xs={24} sm={6}>
                  <p
                    className={
                      'text-xs text-gray-500 mb-1 font-medium'
                    }
                  >
                    Note
                  </p>

                  <Input
                    value={slot.note}
                    placeholder={'e.g. Advance payment'}
                    onChange={e =>
                      updateSlot(
                        slot.id,
                        'note',
                        e.target.value,
                      )
                    }
                  />
                </Col>
              </Row>
            </div>
          ))}
        </div>

        {editablePayments.length > 0 && (
          <div
            className={
              'px-4 py-3 bg-primary-100 border-t border-primary-200 flex flex-wrap gap-4 items-center justify-between'
            }
          >
            <div className={'flex gap-4 text-sm'}>
              <span className={'text-primary-700'}>
                Total Recorded:{' '}
                <b
                  className={
                    totalEditablePaid > (bill.finalAmount ?? 0)
                      ? 'text-red-600'
                      : 'text-primary-900'
                  }
                >
                  {fmtCur(totalEditablePaid)}
                </b>
              </span>

              <span className={'text-gray-500'}>
                Final:{' '}
                <b className={'text-primary-700'}>
                  {fmtCur(bill.finalAmount)}
                </b>
              </span>

              <span className={'text-gray-500'}>
                Entries: <b>{editablePayments.length}</b>
              </span>
            </div>

            <div
              className={`rounded-lg px-4 py-1.5 text-sm font-bold ${
                pendingAfterUpdate > 0
                  ? 'bg-red-50 text-red-600 border border-red-200'
                  : 'bg-green-50 text-green-600 border border-green-200'
              }`}
            >
              Remaining: {fmtCur(pendingAfterUpdate)}
            </div>
          </div>
        )}
      </div>

      {
        totalEditablePaid > (bill.finalAmount ?? 0) && (
          <div
            className={
              'mt-3 p-3 bg-red-50 border border-red-200 rounded-lg'
            }
          >
            <p className={'text-red-600 text-sm m-0'}>
              ⚠️ Total payments ({fmtCur(totalEditablePaid)}) exceed
              the bill amount ({fmtCur(bill.finalAmount)})
            </p>
          </div>
        )
      }
    </Modal>
  );
};

EditBill.propTypes = {
  open: PropTypes.bool,
  bill: PropTypes.object,
  token: PropTypes.string,
  onClose: PropTypes.func,
  onSuccess: PropTypes.func,
};

export default EditBill;
